import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Calendar, Mail, MapPin, Phone } from "lucide-react";
import { http } from "@/lib/api";
import { BookMeeting } from "@/components/BookMeeting";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (window.location.hash === "#book-meeting") {
      setTimeout(() => document.getElementById("book-meeting")?.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post("/contact", form);
      toast.success("Message received — our team will respond within 24 hours.");
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
    } catch {
      toast.error("Could not send. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pt-32 pb-24 max-w-[1400px] mx-auto px-6 lg:px-10">
      <div className="grid lg:grid-cols-12 gap-10 mb-16">
        <div className="lg:col-span-8">
          <div className="eyebrow mb-5">Contact</div>
          <h1 className="font-editorial text-5xl md:text-7xl text-[#1D3A2F] leading-[0.95]">
            Let's build something
            <br />
            <em className="gold-text">amazing together.</em>
          </h1>
        </div>
        <div className="lg:col-span-4 flex items-end">
          <p className="text-lg text-[#222222]/70 leading-relaxed">
            Talk to a founder-specialist. Book a 30-min consultation or send us
            a note — we respond within 24 hours.
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        {/* Contact form */}
        <div className="lg:col-span-7">
          <form
            onSubmit={submit}
            className="rounded-3xl bg-[#FDFBF7] border border-[#D7D4CF]/60 p-8 md:p-12 space-y-5"
          >
            <div className="grid md:grid-cols-2 gap-5">
              <Field
                label="Name"
                required
                value={form.name}
                onChange={(v) => setForm((f) => ({ ...f, name: v }))}
                testid="contact-name"
              />
              <Field
                label="Email"
                type="email"
                required
                value={form.email}
                onChange={(v) => setForm((f) => ({ ...f, email: v }))}
                testid="contact-email"
              />
              <Field
                label="Phone"
                type="tel"
                value={form.phone}
                onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
                testid="contact-phone"
              />
              <Field
                label="Company"
                value={form.company}
                onChange={(v) => setForm((f) => ({ ...f, company: v }))}
                testid="contact-company"
              />
            </div>
            <div>
              <label className="eyebrow block mb-2">Message *</label>
              <textarea
                data-testid="contact-message"
                required
                rows={5}
                value={form.message}
                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                className="w-full px-5 py-3 rounded-2xl bg-[#F7F4EE] border border-[#D7D4CF] text-sm focus:outline-none focus:border-[#1D3A2F] resize-none"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="contact-submit"
              className="inline-flex items-center gap-3 h-13 py-4 px-8 rounded-full bg-[#1D3A2F] text-[#FDFBF7] text-[12px] tracking-[0.18em] uppercase font-medium hover:bg-black transition disabled:opacity-70"
            >
              {busy ? "Sending…" : "Send Message"}
            </button>
          </form>
        </div>

        {/* Aside */}
        <aside className="lg:col-span-5 space-y-6">
          <div className="rounded-3xl bg-[#1D3A2F] text-[#FDFBF7] p-8">
            <div className="eyebrow text-[#FDFBF7]/60 mb-4">
              Schedule a Meeting
            </div>
            <h3 className="font-editorial text-3xl">Talk to Expert</h3>
            <p className="text-sm text-[#FDFBF7]/70 mt-3">
              30-min strategy session — formulation review, launch plan and
              MOQ discussion.
            </p>
            <a
              href="#book-meeting"
              data-testid="contact-book-meeting-cta"
              className="btn-gold mt-6 inline-flex items-center gap-3 h-12 px-6 rounded-full text-[12px] tracking-[0.16em] uppercase font-bold"
            >
              <Calendar className="w-4 h-4" /> Book a Meeting
            </a>
          </div>

          <div className="rounded-3xl bg-[#FDFBF7] border border-[#D7D4CF]/60 p-8 space-y-6">
            <InfoRow
              icon={Phone}
              label="Customer Service"
              value="+91-92742 59625 · +91-84880 00000"
            />
            <InfoRow
              icon={Mail}
              label="Email"
              value="sales@fresioherbals.com"
              href="mailto:sales@fresioherbals.com"
            />
            <InfoRow
              icon={MapPin}
              label="Office"
              value="1782, Savli main road, Near Manjusar GIDC, Vadodara, Gujarat 391775"
            />
          </div>
        </aside>
      </div>

      <section id="book-meeting" data-testid="contact-book-meeting" className="mt-24 scroll-mt-28">
        <div className="eyebrow mb-4 !text-[#A68840]">Book a Meeting</div>
        <h2 className="font-editorial text-5xl md:text-6xl text-[#1D3A2F] leading-[1.02] mb-12">
          Pick a slot. <em className="gold-text">We'll be there.</em>
        </h2>
        <div className="rounded-[2.5rem] glass-card hero-mesh p-6 sm:p-10 lg:p-14">
          <BookMeeting />
        </div>
      </section>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, required, testid }) {
  return (
    <div>
      <label className="eyebrow block mb-2">
        {label} {required && <span className="text-[#556B57]">*</span>}
      </label>
      <input
        data-testid={testid}
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-12 px-5 rounded-full bg-[#F7F4EE] border border-[#D7D4CF] text-sm focus:outline-none focus:border-[#1D3A2F]"
      />
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, href }) {
  const inner = (
    <>
      <div className="w-10 h-10 rounded-full bg-[#F7F4EE] flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-[#1D3A2F]" strokeWidth={1.5} />
      </div>
      <div>
        <div className="eyebrow mb-1">{label}</div>
        <div className="text-[#1D3A2F]">{value}</div>
      </div>
    </>
  );
  return href ? (
    <a href={href} className="flex items-start gap-4 hover:opacity-80">{inner}</a>
  ) : (
    <div className="flex items-start gap-4">{inner}</div>
  );
}
